package app.smartcoding.realrate.presentation.components

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.*
import androidx.compose.foundation.Canvas
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Fill
import kotlinx.coroutines.launch
import kotlin.math.PI
import kotlin.math.sin

@Composable
fun HeaderWaveBackground(
    modifier: Modifier = Modifier,
    currentRate: Double,
    currentAppreciation: Double
) {
    val normalizedRate = (currentRate / 15.0).coerceIn(0.0, 1.0).toFloat()

    val normalizedAppreciation = ((currentAppreciation - 1.0) * 3).coerceIn(0.0, 1.0).toFloat()

    val targetVolatility = (normalizedRate * 1.2f).coerceIn(0.0f, 1.0f)

    val targetAmplitude = 0.1f + (normalizedAppreciation * 0.25f)

    val targetSpeed = 1.0f + (normalizedRate * 1.5f)

    val animVolatility = remember { Animatable(targetVolatility) }
    val animAmplitude = remember { Animatable(targetAmplitude) }
    val animSpeed = remember { Animatable(targetSpeed) }

    LaunchedEffect(targetVolatility, targetAmplitude, targetSpeed) {
        launch { animVolatility.animateTo(targetVolatility, tween(2000, easing = LinearOutSlowInEasing)) }
        launch { animAmplitude.animateTo(targetAmplitude, tween(2000, easing = LinearOutSlowInEasing)) }
        launch { animSpeed.animateTo(targetSpeed, tween(2000, easing = LinearOutSlowInEasing)) }
    }

    val targetColor = when {
        currentRate < 5.0 -> MaterialTheme.colorScheme.primary
        currentRate < 10.0 -> MaterialTheme.colorScheme.tertiary
        else -> MaterialTheme.colorScheme.error
    }
    val animatedColor by animateColorAsState(targetColor, tween(1500))

    val infiniteTransition = rememberInfiniteTransition(label = "WavePhase")
    val phase by infiniteTransition.animateFloat(
        initialValue = 0f,
        targetValue = 2 * PI.toFloat(),
        animationSpec = infiniteRepeatable(
            animation = tween(8000, easing = LinearEasing),
            repeatMode = RepeatMode.Restart
        )
    )

    Canvas(modifier = modifier) {
        val width = size.width
        val height = size.height

        val amplitude = height * animAmplitude.value
        val volatility = animVolatility.value
        val speed = animSpeed.value
        val currentPhase = phase * speed

        drawFinancialWave(
            color = animatedColor.copy(alpha = 0.1f),
            width = width,
            height = height,
            amplitude = amplitude * 0.3f,
            frequency = 1.0f,
            phase = currentPhase * 0.8f,
            volatility = volatility * 0.3f,
            harmonics = listOf(1.0f, 0.5f)
        )

        drawFinancialWave(
            color = animatedColor.copy(alpha = 0.2f),
            width = width,
            height = height,
            amplitude = amplitude,
            frequency = 1.2f,
            phase = currentPhase + 1.0f,
            volatility = volatility * 0.6f,
            harmonics = listOf(1.0f, 0.4f, 0.2f)
        )

        drawFinancialWave(
            brush = Brush.verticalGradient(
                colors = listOf(animatedColor.copy(alpha = 0.5f), animatedColor.copy(alpha = 0.0f)),
                startY = height * 0.2f,
                endY = height
            ),
            width = width,
            height = height,
            amplitude = amplitude * 1.2f,
            frequency = 1.5f,
            phase = currentPhase + 2.0f,
            volatility = volatility,
            harmonics = listOf(1.0f, 0.5f, 0.25f, 0.125f)
        )
    }
}

fun DrawScope.drawFinancialWave(
    color: Color? = null,
    brush: Brush? = null,
    width: Float,
    height: Float,
    amplitude: Float,
    frequency: Float,
    phase: Float,
    volatility: Float,
    harmonics: List<Float>
) {
    val path = Path()
    val points = 120
    val step = width / points

    path.moveTo(0f, height)

    for (i in 0..points) {
        val x = i * step
        val normX = i.toFloat() / points

        var ySignal = 0.0
        harmonics.forEachIndexed { idx, hAmp ->
            val hFreq = frequency * (idx + 1)
            // Base wave (idx=0) is always present. Higher harmonics depend on volatility.
            val hVol = if (idx == 0) 1.0f else volatility
            ySignal += sin(normX * 2 * PI * hFreq + phase + (idx * 1.5)) * hAmp * hVol
        }

        val y = (height * 0.65f) + (ySignal * amplitude)

        if (i == 0) path.lineTo(x, y.toFloat()) else path.lineTo(x, y.toFloat())
    }

    path.lineTo(width, height)
    path.lineTo(0f, height)
    path.close()

    if (brush != null) {
        drawPath(path, brush, style = Fill)
    } else if (color != null) {
        drawPath(path, color, style = Fill)
    }
}
